import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v3";
import { Bot, Key, Shield, Crown, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import type { ModalComponentProps } from "@/lib/modal";
import { Button } from "@/components/ui/button";
import {
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCreateCustomBot } from "@/lib/api/applications";
import { useSubscription } from "@/lib/api/subscription";
import { isSubscriptionError } from "@/lib/api/errors";

const createCustomBotSchema = z.object({
  name: z.string().min(1, "Bot name is required").max(100),
  discordBotToken: z.string().min(1, "Bot token is required"),
});

type CreateCustomBotValues = z.infer<typeof createCustomBotSchema>;

export interface CreateCustomBotModalProps {
  tenants: Array<{ id: string; name: string; type: string; role: string }>;
}

export function CreateCustomBotModal({
  tenants,
  onClose,
}: CreateCustomBotModalProps & ModalComponentProps) {
  const navigate = useNavigate();
  const createCustomBot = useCreateCustomBot();

  const ownerTenant = tenants.find((t) => t.role === "owner");
  const tenantId = ownerTenant?.id ?? "";

  const { data: subscription } = useSubscription(tenantId);
  const isLocked = subscription?.data?.features?.customBot === false;

  const form = useForm<CreateCustomBotValues>({
    resolver: zodResolver(createCustomBotSchema),
    defaultValues: {
      name: "",
      discordBotToken: "",
    },
  });

  const onSubmit = (values: CreateCustomBotValues) => {
    createCustomBot.mutate(
      { ...values, tenantId },
      {
        onSuccess: (result) => {
          form.reset();
          onClose();
          navigate({
            to: "/dashboard/applications/$applicationId",
            params: { applicationId: result.data.applicationId },
          });
        },
        onError: (error) => {
          if (isSubscriptionError(error) && error.code === "FEATURE_REQUIRED") {
            toast.error("Custom bots require a plan upgrade", {
              action: {
                label: "Upgrade",
                onClick: () => {
                  onClose();
                  navigate({
                    to: "/dashboard/teams/$tenantId",
                    params: { tenantId },
                    search: { tab: "subscription" },
                  });
                },
              },
            });
            return;
          }

          const message =
            error instanceof Error ? error.message : "Failed to create bot";

          if (message.toLowerCase().includes("invalid bot token")) {
            form.setError("discordBotToken", {
              message: "Invalid Discord bot token. Please check and try again.",
            });
            return;
          }

          toast.error(message);
        },
      }
    );
  };

  return (
    <>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>Create Custom Bot</ResponsiveModalTitle>
        <ResponsiveModalDescription>
          Connect your own Discord bot to use with your application.
        </ResponsiveModalDescription>
      </ResponsiveModalHeader>

      <div className="mt-2 flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
        <Shield className="size-4 text-rose-500" />
        <span className="text-muted-foreground">Team:</span>
        <span className="font-medium">{ownerTenant?.name}</span>
      </div>

      <div className="group/lock relative mt-2">
        {isLocked && (
          <>
            <div className="absolute inset-0 z-10" />
            <div className="pointer-events-auto absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
              <Link
                to="/dashboard/teams/$tenantId"
                params={{ tenantId }}
                search={{ tab: "subscription" }}
                onClick={onClose}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-rose-500 to-red-500 px-3 py-1.5 text-xs font-medium text-white shadow-lg transition-opacity hover:opacity-90"
              >
                <Crown className="size-3.5" />
                Unlock with Pro
                <ArrowRight className="size-3" />
              </Link>
            </div>
          </>
        )}
        <div
          className={
            isLocked
              ? "pointer-events-none select-none blur-[2px] grayscale-[30%]"
              : undefined
          }
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Bot className="size-4 text-rose-500" />
                      Bot Name
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="My Custom Bot" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="discordBotToken"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <Key className="size-4 text-rose-500" />
                      Discord Bot Token
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ResponsiveModalFooter>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createCustomBot.isPending}>
                  {createCustomBot.isPending ? "Creating..." : "Create Bot"}
                </Button>
              </ResponsiveModalFooter>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
}

CreateCustomBotModal.displayName = "CreateCustomBotModal";
